<template>
  <div v-if="isVisible" class="delete-blocked-financial-root">
    <div
      class="modal fade show delete-blocked-financial-modal"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      style="display: block"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("deleteBlockedFinancial.title") }}</h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>
          <div class="modal-body">
            <p v-if="summaryText" class="text-muted mb-2">{{ summaryText }}</p>
            <p class="mb-3">{{ introText }}</p>

            <div
              v-for="entity in entities"
              :key="`${entity.model}-${entity.id}`"
              class="delete-blocked-entity"
            >
              <a
                href="#"
                class="delete-blocked-entity__title"
                @click.prevent="openRef(entity)"
              >
                <i class="fas fa-external-link-alt me-1" aria-hidden="true"></i>
                {{ entity.name }}
              </a>

              <div
                v-for="section in sectionsFor(entity)"
                :key="section.key"
                class="delete-blocked-section"
              >
                <div class="delete-blocked-section__label">
                  {{ section.label }}
                </div>
                <ul class="delete-blocked-section__list">
                  <li
                    v-for="ref in section.items"
                    :key="`${ref.model}-${ref.id}`"
                  >
                    <a
                      v-if="canOpen(ref)"
                      href="#"
                      @click.prevent="openRef(ref)"
                    >
                      {{ ref.name }}
                      <i
                        class="fas fa-external-link-alt delete-blocked-link-icon"
                        aria-hidden="true"
                      ></i>
                    </a>
                    <span v-else>{{ ref.name }}</span>
                  </li>
                </ul>
              </div>

              <p
                v-if="entity.errorText && !sectionsFor(entity).length && !(entity.shippings || []).length"
                class="delete-blocked-fallback-error"
              >
                {{ entity.errorText }}
              </p>

              <div
                v-for="ship in entity.shippings || []"
                :key="`ship-${ship.id}`"
                class="delete-blocked-shipping"
              >
                <a
                  href="#"
                  class="delete-blocked-shipping__title"
                  @click.prevent="openRef(ship)"
                >
                  <i class="fas fa-ship me-1" aria-hidden="true"></i>
                  {{ $t("Shipping") }}: {{ ship.name }}
                </a>
                <div
                  v-for="section in sectionsFor(ship)"
                  :key="`ship-${ship.id}-${section.key}`"
                  class="delete-blocked-section"
                >
                  <div class="delete-blocked-section__label">
                    {{ section.label }}
                  </div>
                  <ul class="delete-blocked-section__list">
                    <li
                      v-for="ref in section.items"
                      :key="`${ref.model}-${ref.id}`"
                    >
                      <a
                        v-if="canOpen(ref)"
                        href="#"
                        @click.prevent="openRef(ref)"
                      >
                        {{ ref.name }}
                        <i
                          class="fas fa-external-link-alt delete-blocked-link-icon"
                          aria-hidden="true"
                        ></i>
                      </a>
                      <span v-else>{{ ref.name }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t("Close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" @click="close"></div>
  </div>
</template>

<script>
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";

const OPENABLE_MODELS = new Set([
  "proform",
  "shipping",
  "payment",
  "collect",
  "notacomercial",
  "contact",
  "bankaccount",
]);

export default {
  name: "DeleteBlockedFinancialModal",
  props: {
    isVisible: { type: Boolean, default: false },
    entities: { type: Array, default: () => [] },
    summaryText: { type: String, default: "" },
  },
  computed: {
    introText() {
      const models = [
        ...new Set(
          (this.entities || [])
            .map((e) => String((e && e.model) || "").toLowerCase())
            .filter(Boolean)
        ),
      ];
      if (models.length === 1 && models[0] === "proform") {
        return this.$t("deleteBlockedFinancial.introProforms");
      }
      if (models.length === 1 && models[0] === "shipping") {
        return this.$t("deleteBlockedFinancial.introShippings");
      }
      if (models.length === 1 && models[0] === "contact") {
        return this.$t("deleteBlockedFinancial.introContacts");
      }
      return this.$t("deleteBlockedFinancial.introGeneric");
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    canOpen(ref) {
      if (!ref || !ref.id) return false;
      return OPENABLE_MODELS.has(String(ref.model || "").toLowerCase());
    },
    openRef(ref) {
      if (!this.canOpen(ref)) return;
      const model = String(ref.model).toLowerCase();
      const url =
        buildDynamicModelItemViewPath(model, ref.id, ref.name) ||
        `/dynamic-model/${encodeURIComponent(model)}/view/${encodeURIComponent(
          String(ref.id)
        )}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    sectionsFor(entity) {
      if (!entity) return [];
      const sections = [
        {
          key: "payments",
          label: this.$t("Payments"),
          items: entity.payments || [],
        },
        {
          key: "collects",
          label: this.$t("Collects"),
          items: entity.collects || [],
        },
        {
          key: "notasComerciales",
          label: this.$t("deleteBlockedFinancial.commercialNotes"),
          items: entity.notasComerciales || [],
        },
        {
          key: "expenseShippings",
          label: this.$t("deleteBlockedFinancial.expenseShippings"),
          items: entity.expenseShippings || [],
        },
        {
          key: "clientShippings",
          label: this.$t("deleteBlockedFinancial.clientShippings"),
          items: entity.clientShippings || [],
        },
        {
          key: "bankAccounts",
          label: this.$t("deleteBlockedFinancial.bankAccounts"),
          items: entity.bankAccounts || [],
        },
      ];
      return sections.filter((s) => Array.isArray(s.items) && s.items.length > 0);
    },
  },
};
</script>

<style scoped>
.delete-blocked-financial-root {
  position: relative;
  z-index: 1060;
}
.delete-blocked-financial-modal {
  z-index: 1065;
}
.delete-blocked-financial-root > .modal-backdrop {
  z-index: 1060;
}
.delete-blocked-entity {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #fafafa;
}
.delete-blocked-entity__title {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  font-size: 1.05rem;
  color: #0d6efd;
  text-decoration: none;
  margin-bottom: 8px;
}
.delete-blocked-entity__title:hover {
  text-decoration: underline;
}
.delete-blocked-section {
  margin-top: 8px;
  margin-left: 8px;
}
.delete-blocked-section__label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 2px;
}
.delete-blocked-section__list {
  list-style: none;
  margin: 0 0 4px;
  padding-left: 16px;
}
.delete-blocked-section__list li {
  margin: 2px 0;
}
.delete-blocked-section__list a {
  color: #0d6efd;
  text-decoration: none;
}
.delete-blocked-section__list a:hover {
  text-decoration: underline;
}
.delete-blocked-link-icon {
  font-size: 0.7rem;
  margin-left: 4px;
  opacity: 0.7;
}
.delete-blocked-shipping {
  margin-top: 12px;
  margin-left: 8px;
  padding: 10px 12px;
  border-left: 3px solid #cbd5e1;
  background: #fff;
  border-radius: 0 6px 6px 0;
}
.delete-blocked-shipping__title {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  color: #0d6efd;
  text-decoration: none;
  margin-bottom: 4px;
}
.delete-blocked-shipping__title:hover {
  text-decoration: underline;
}
.delete-blocked-fallback-error {
  margin: 8px 0 0;
  font-size: 0.9rem;
  color: #6b7280;
  white-space: pre-wrap;
}
</style>
